/**
 * ECIES over secp256k1 — encrypts on-chain payloads to an organization's
 * public key. Byte-for-byte identical to what other bindings produce
 * because the ECIES implementation lives in the Rust core.
 *
 * Only the holder of the org's private key can decrypt. Everyone else —
 * including anyone querying the Chromia node directly, and Atbash itself
 * — sees ciphertext.
 *
 * ─── WIRE FORMAT (FROZEN) ────────────────────────────────────────────
 *   version           1 byte    = 0x01
 *   ephemeral_pubkey  33 bytes    compressed secp256k1 point
 *   nonce             12 bytes    random, per message
 *   ciphertext+tag    N bytes     AES-256-GCM output (16-byte tag appended)
 *
 * Version 0x01 is FROZEN. Records under it already exist on immutable
 * ledgers; evolving the format means a NEW version byte and preserving
 * the 0x01 decrypt path in every binding, forever.
 *
 * Domain separation invariants (implemented by the Rust core):
 *   salt = ephemeral pubkey  — ties the key to this exact handshake
 *   info = domain            — a verdict payload cannot be read as a tool call
 *   aad  = domain|record_id  — a payload cannot be lifted onto another row
 */

import { native } from "../native.js";

/** Wire-format version byte. Frozen at 0x01. */
export const FORMAT_VERSION = native.ECIES_FORMAT_VERSION;

/**
 * Cryptographic domain per payload kind. Each kind derives a distinct
 * key from the same handshake — a verdict payload handed to the
 * tool-call reader fails authentication rather than silently decoding.
 *
 * Values here are the SHORT domain names the Rust core recognizes.
 * The full HKDF `info` strings (`atbash:chain-encryption:v1:<kind>`)
 * live inside the core and never surface at the API boundary.
 */
export const EciesDomain = {
  toolCall: "toolcall",
  verdict: "verdict",
  note: "note",
  policy: "policy",
  raw: "raw",
} as const;
export type EciesDomain = (typeof EciesDomain)[keyof typeof EciesDomain];

/**
 * Encrypt `plaintext` so that only the holder of `orgPubKeyHex` can read it.
 *
 * @param plaintext     UTF-8 text to protect.
 * @param orgPubKeyHex  Org's compressed secp256k1 public key (33 bytes hex).
 * @param aad           Context bound to the ciphertext — pass the record's id.
 * @returns raw payload for a Rell `byte_array` column.
 */
export function encryptForOrg(
  plaintext: string,
  orgPubKeyHex: string,
  aad: string,
  domain: EciesDomain = EciesDomain.raw,
): Uint8Array {
  return native.encryptForOrg(plaintext, orgPubKeyHex, aad, domain);
}

/**
 * Decrypt a payload produced by {@link encryptForOrg}.
 *
 * Throws if the key is wrong, the `aad` does not match the one used at
 * encrypt time, or the ciphertext was tampered with — GCM authentication
 * makes all three indistinguishable by design.
 *
 * @param payload        Value read from the on-chain `byte_array` column.
 * @param orgPrivKeyHex  Org's secp256k1 private key (32 bytes hex).
 * @param aad            Must equal the `aad` used when encrypting.
 */
export function decryptForOrg(
  payload: Uint8Array,
  orgPrivKeyHex: string,
  aad: string,
  domain: EciesDomain = EciesDomain.raw,
): string {
  return native.decryptForOrg(
    Buffer.isBuffer(payload) ? payload : Buffer.from(payload),
    orgPrivKeyHex,
    aad,
    domain,
  );
}

/**
 * Size in bytes of the encrypted payload for a given plaintext length.
 * Lets callers check against the on-chain column cap
 * (`MAX_CONTENT_CIPHER_SIZE`) before submitting a transaction the
 * contract would reject.
 */
export function encryptedLength(plaintextByteLength: number): number {
  return native.encryptedLength(plaintextByteLength);
}
